import InfiniteScroll from 'react-infinite-scroll-component';
import { Role } from 'auth';
import { useCallback, useContext, useEffect, useState } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import ArchiveSwitch from '@/modules/common/components/ArchiveSwitch.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { FormItem } from '@/modules/admin-dashboard/components/items/FormItem.tsx';
import { CreateFormModal } from '@/modules/admin-dashboard/components/modals/CreateFormModal.tsx';
import CreateRegisterModal from '@/modules/admin-dashboard/components/modals/CreateRegisterModal.tsx';
import { useCurrentUserFormsListQuery } from '@/modules/admin-dashboard/queries/forms.ts';
import { Button, EmptyListState, ModalProviderContext, PageLayout, SearchField, Spinner } from 'ui';

interface FormsProps {
  tagId?: number;
}

interface FormsFilters {
  archive: boolean;
}

const Forms = ({ tagId }: FormsProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const businessId = useGetCurrentBusinessId();
  const [filters, setFilters] = useState<FormsFilters>({
    archive: false,
  });
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setFilters({
      archive: false,
    });
    setSearchValue('');
  }, [businessId, tagId]);

  const handleArchiveChange = useCallback(
    (archive: boolean) => {
      setFilters(filters => ({ ...filters, archive }));
    },
    [setFilters],
  );

  const {
    hasNextPage: formsListHasNextPage,
    fetchNextPage: fetchNextFormsListPage,
    data: formsData,
    isLoading,
  } = useCurrentUserFormsListQuery({
    page: 1,
    page_size: 18,
    filter_by_name: searchValue,
    filter_by_tag_ids: tagId ? tagId.toString() : undefined,
    filter_by_deleted: filters.archive,
  });

  const forms = (formsData?.pages || []).flatMap(({ data }) => data);

  const headerAction = (
    <div className="flex flex-1 max-w-[660px] flex-wrap-reverse w-full justify-end items-center gap-3">
      <ArchiveSwitch active={filters.archive} onChange={handleArchiveChange} />
      <SearchField
        className="w-[220px]"
        placeholder="Search"
        value={searchValue}
        onChange={setSearchValue}
      />
      <Button variant="secondary" className="w-fit" onClick={() => openModal(CreateRegisterModal)}>
        New label
      </Button>
      <Button
        className="w-fit"
        onClick={() => openModal(CreateFormModal, { tag_ids: tagId ? [tagId] : [] })}
      >
        New form
      </Button>
    </div>
  );
  return (
    <PageLayout title="Forms" headerAction={headerAction}>
      <EmptyListState
        title="No forms created yet"
        description="Form is the core entity which allows to ask employees about compliance with certain registers. Once you created your first form, create a task with it from the Tasks page and assign it to a user."
        isLoading={isLoading}
        isSearchUsed={!!searchValue || !!tagId || filters.archive}
        itemsCount={forms.length}
      />
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      )}
      {!!forms.length && (
        <InfiniteScroll
          next={fetchNextFormsListPage}
          dataLength={forms.length}
          hasMore={!!formsListHasNextPage}
          className="w-full flex flex-col space-y-2"
          style={{ overflow: 'unset' }}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          scrollableTarget="scrollable"
        >
          {forms.map(form => (
            <FormItem key={form.id} form={form} />
          ))}
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(Forms, Role.COMPLIANCE_ADMIN);
